import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import axios from 'axios';
import { MapPin, LogOut, Plus, Edit2, Trash2, Search, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const AdminDashboard = () => {
  const { adminToken, adminLogout, isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [parkingLots, setParkingLots] = useState([]);
  const [filteredLots, setFilteredLots] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalLots: 0, totalSpots: 0, availableSpots: 0 });
  const [editingLot, setEditingLot] = useState(null);
  
  // Payouts state
  const [activeTab, setActiveTab] = useState('parkings');
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [paymentSystemStatus, setPaymentSystemStatus] = useState({ enabled: false, loading: true });

  useEffect(() => {
    if (!isAdminAuthenticated) {
      navigate('/admin-login');
    }
  }, [isAdminAuthenticated, navigate]);

  useEffect(() => {
    console.log('🔧 AdminDashboard: Payment system component loaded');
    fetchParkingLots();
    fetchPaymentSystemStatus();
  }, []);

  const fetchPaymentSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPaymentSystemStatus({ ...response.data, loading: false });
    } catch (error) {
      console.error('Error fetching payment status:', error);
      setPaymentSystemStatus({ enabled: false, loading: false });
    }
  };

  useEffect(() => {
    const filtered = parkingLots.filter(lot => 
      lot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lot.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lot.owner_email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLots(filtered);
  }, [searchTerm, parkingLots]);

  const fetchParkingLots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/parking-lots`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setParkingLots(response.data);
      setFilteredLots(response.data);
      
      // Calculate stats
      const totalSpots = response.data.reduce((sum, lot) => sum + lot.total_spots, 0);
      const availableSpots = response.data.reduce((sum, lot) => sum + lot.available_spots, 0);
      setStats({
        totalLots: response.data.length,
        totalSpots,
        availableSpots
      });
    } catch (error) {
      console.error('Error fetching parking lots:', error);
      toast.error('Erro ao carregar estacionamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este estacionamento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/parking-lots/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Estacionamento excluído com sucesso!');
      fetchParkingLots();
    } catch (error) {
      console.error('Error deleting parking lot:', error);
      toast.error('Erro ao excluir estacionamento');
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin-login');
    toast.success('Logout realizado com sucesso!');
  };

  const fetchPendingPayouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-payouts`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPendingPayouts(response.data);
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
      toast.error('Erro ao buscar pagamentos pendentes');
    }
  };

  const handleCreatePayout = async () => {
    if (!selectedOwner || !payoutAmount) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      await axios.post(`${API}/admin/create-payout`, null, {
        params: {
          owner_id: selectedOwner.owner_id,
          amount: parseFloat(payoutAmount),
          notes: payoutNotes
        },
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Pagamento registrado com sucesso!');
      setShowBankModal(false);
      setSelectedOwner(null);
      setPayoutAmount('');
      setPayoutNotes('');
      fetchPendingPayouts();
    } catch (error) {
      console.error('Error creating payout:', error);
      toast.error('Erro ao criar pagamento');
    }
  };

  const handleCompletePayout = async (payoutId) => {
    try {
      await axios.put(`${API}/admin/complete-payout/${payoutId}`, null, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Pagamento marcado como concluído!');
      fetchPendingPayouts();
    } catch (error) {
      console.error('Error completing payout:', error);
      toast.error('Erro ao completar pagamento');
    }
  };

  useEffect(() => {
    if (activeTab === 'payouts') {
      fetchPendingPayouts();
    }
  }, [activeTab]);

  const getTypeColor = (type) => {
    const colors = {
      indoor: '#302681',
      outdoor: '#009440',
      mixed: '#FFCB00'
    };
    return colors[type] || colors.indoor;
  };

  const getTypeName = (type) => {
    const names = {
      indoor: 'Coberto',
      outdoor: 'Descoberto',
      mixed: 'Misto'
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#302681] mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-8 h-8" style={{ color: '#302681' }} />
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#302681' }}>ParkingSpot Admin</h1>
                <p className="text-xs text-gray-500">Painel de Administração</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment System Configuration Alert */}
        <div className={`mb-6 border-l-4 p-6 rounded-r-lg ${
          paymentSystemStatus.enabled 
            ? 'bg-green-50 border-green-400' 
            : 'bg-blue-50 border-blue-400'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {paymentSystemStatus.enabled ? (
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-lg font-semibold mb-2 ${
                paymentSystemStatus.enabled ? 'text-green-900' : 'text-blue-900'
              }`}>
                🔧 Configuração do Sistema de Pagamentos
              </h3>
              <div className={`text-sm space-y-2 ${
                paymentSystemStatus.enabled ? 'text-green-800' : 'text-blue-800'
              }`}>
                <p>
                  <strong>Status:</strong>{' '}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    paymentSystemStatus.enabled 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {paymentSystemStatus.loading 
                      ? 'Verificando...' 
                      : paymentSystemStatus.enabled 
                        ? 'Ativo e Configurado' 
                        : 'Não Configurado'}
                  </span>
                </p>
                <p>
                  <strong>Provedor:</strong> Mercado Pago
                </p>
                
                {!paymentSystemStatus.enabled && (
                  <>
                    <p className="mt-2">
                      Para habilitar pagamentos online, você precisará:
                    </p>
                    <ol className="list-decimal list-inside ml-4 space-y-1">
                      <li>Criar conta no Mercado Pago Developers</li>
                      <li>Obter Access Token e Public Key</li>
                      <li>Configurar as credenciais no sistema</li>
                      <li>Ativar o sistema de pagamentos (MERCADO_PAGO_ENABLED=true)</li>
                    </ol>
                  </>
                )}
                
                <p className="mt-3 font-medium">
                  {paymentSystemStatus.enabled 
                    ? '✅ O sistema está pronto para receber pagamentos PIX e Cartão.' 
                    : '📌 Uma vez configurado, proprietários poderão receber pagamentos via PIX/QR Code diretamente dos usuários.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('parkings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'parkings'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Estacionamentos
            </button>
            <button
              onClick={() => setActiveTab('payouts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'payouts'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pagamentos & Comissões
            </button>
          </nav>
        </div>

        {/* Parkings Tab Content */}
        {activeTab === 'parkings' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total de Estacionamentos</p>
                <p className="text-3xl font-bold" style={{ color: '#302681' }}>{stats.totalLots}</p>
              </div>
              <BarChart3 className="w-12 h-12 text-gray-300" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total de Vagas</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalSpots}</p>
              </div>
              <BarChart3 className="w-12 h-12 text-gray-300" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Vagas Disponíveis</p>
                <p className="text-3xl font-bold text-green-600">{stats.availableSpots}</p>
              </div>
              <BarChart3 className="w-12 h-12 text-gray-300" />
            </div>
          </div>
        </div>

        {/* Parking Lots Management */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Gerenciar Estacionamentos</h2>
            
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#302681] focus:border-transparent"
                placeholder="Buscar por nome, endereço ou proprietário..."
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endereço</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proprietário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vagas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço/h</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLots.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      Nenhum estacionamento encontrado
                    </td>
                  </tr>
                ) : (
                  filteredLots.map((lot) => (
                    <tr key={lot.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lot.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">{lot.address}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{lot.owner_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white"
                          style={{ backgroundColor: getTypeColor(lot.parking_type) }}
                        >
                          {getTypeName(lot.parking_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lot.available_spots}/{lot.total_spots}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">R$ {lot.price_per_hour.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDelete(lot.id)}
                          className="text-red-600 hover:text-red-900 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {/* Payouts Tab Content */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Total Pendente</h3>
                <p className="text-3xl font-bold text-purple-700">
                  R$ {pendingPayouts.reduce((sum, p) => sum + p.pending_payout, 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {pendingPayouts.length} proprietários aguardando
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Comissão da Plataforma</h3>
                <p className="text-3xl font-bold text-green-700">
                  R$ {pendingPayouts.reduce((sum, p) => sum + p.platform_commission, 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">10% das transações</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Receita Total</h3>
                <p className="text-3xl font-bold text-blue-700">
                  R$ {pendingPayouts.reduce((sum, p) => sum + p.total_revenue, 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Todas as transações</p>
              </div>
            </div>

            {/* Pending Payouts Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Pagamentos Pendentes</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Proprietários aguardando transferência bancária
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Proprietário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Receita Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Comissão (10%)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        A Pagar
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Transações
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingPayouts.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          Nenhum pagamento pendente no momento
                        </td>
                      </tr>
                    ) : (
                      pendingPayouts.map((payout) => (
                        <tr key={payout.owner_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {payout.owner_name || 'N/A'}
                              </p>
                              <p className="text-sm text-gray-500">{payout.owner_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-900">
                            R$ {payout.total_revenue.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            R$ {payout.platform_commission.toFixed(2)}
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold text-green-600">
                              R$ {payout.pending_payout.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {payout.transaction_count}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => {
                                setSelectedOwner(payout);
                                setPayoutAmount(payout.pending_payout.toFixed(2));
                                setShowBankModal(true);
                              }}
                              className="px-4 py-2 bg-[#302681] text-white text-sm rounded hover:opacity-90 transition"
                            >
                              Ver Dados & Pagar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Bank Details Modal */}
        {showBankModal && selectedOwner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Dados Bancários - {selectedOwner.owner_name}
                    </h2>
                    <p className="text-sm text-gray-600">{selectedOwner.owner_email}</p>
                  </div>
                  <button
                    onClick={() => setShowBankModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Payment Summary */}
                <div className="bg-gradient-to-r from-[#302681] to-[#831D1C] text-white p-6 rounded-lg mb-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm opacity-90">Receita Total</p>
                      <p className="text-2xl font-bold">R$ {selectedOwner.total_revenue.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-90">Comissão (10%)</p>
                      <p className="text-2xl font-bold">R$ {selectedOwner.platform_commission.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-90">A Transferir</p>
                      <p className="text-2xl font-bold">R$ {selectedOwner.pending_payout.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Bank Account Details */}
                {selectedOwner.bank_account ? (
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="font-semibold text-lg mb-4">Informações Bancárias</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Banco</p>
                        <p className="font-medium">{selectedOwner.bank_account.bank_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Tipo de Conta</p>
                        <p className="font-medium">
                          {selectedOwner.bank_account.account_type === 'checking' ? 'Corrente' : 'Poupança'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Agência</p>
                        <p className="font-medium">{selectedOwner.bank_account.agency || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Conta</p>
                        <p className="font-medium">{selectedOwner.bank_account.account_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Titular</p>
                        <p className="font-medium">{selectedOwner.bank_account.account_holder_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">CPF/CNPJ</p>
                        <p className="font-medium">{selectedOwner.bank_account.cpf_cnpj || 'N/A'}</p>
                      </div>
                      {selectedOwner.bank_account.pix_key && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-600">Chave PIX</p>
                          <p className="font-medium">{selectedOwner.bank_account.pix_key}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <p className="text-yellow-800">
                      ⚠️ Proprietário ainda não cadastrou dados bancários
                    </p>
                  </div>
                )}

                {/* Payout Form */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor a Transferir
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações (Opcional)
                    </label>
                    <textarea
                      value={payoutNotes}
                      onChange={(e) => setPayoutNotes(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      rows="3"
                      placeholder="Ex: Transferência via PIX, comprovante enviado por email..."
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBankModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreatePayout}
                    className="flex-1 px-4 py-2 bg-[#302681] text-white rounded-md hover:opacity-90"
                  >
                    Registrar Pagamento
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
